import { For, Show } from "solid-js";
import { Archive, ListTodo, MoreVertical, Pencil, Trash2 } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { cn } from "@time-tracker/shared";
import type { Project } from "@time-tracker/powersync";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar } from "@/components/ui/avatar";
import { useI18n } from "@/providers/i18n-provider";
import { getColorClass, getStatusClass, getStatusLabel } from "@/features/chronos-sync/helpers";
import { useEntrySwipeGestures } from "@/features/chronos-sync/hooks/use-entry-swipe-gestures";

const MAX_VISIBLE_MEMBERS = 3;

interface ProjectCardMember {
  id: string;
  name: string | null;
  avatar: string | null;
}

interface ProjectCardItem extends Project {
  durationStr: string;
  members: ProjectCardMember[];
}

interface ProjectCardProps {
  project: ProjectCardItem;
  isMenuOpen: () => boolean;
  setMenuOpen: (open: boolean) => void;
  onEdit: (p: Project) => void;
  onTasks: (p: Project) => void;
  onToggleArchive: (p: Project) => void;
  onDelete: (p: Project) => void;
}

const COMMIT_TRANSITION = "transform 300ms cubic-bezier(0.32, 0.72, 0, 1)";

/**
 * One project card with swipe-to-action on touch (mirrors the Activity Timeline
 * rows via the same `useEntrySwipeGestures`/`TODO_ROW_SWIPE` preset):
 *   - swipe right = edit,
 *   - swipe left  = delete.
 * The `MoreVertical` menu (Edit / Tasks / Archive / Delete) stays as the
 * keyboard and desktop path, so the swipe only invokes the exact same handlers
 * and a11y is untouched. Archive/Tasks remain menu-only (no third swipe axis).
 */
export function ProjectCard(props: ProjectCardProps) {
  const { t } = useI18n();

  let surfaceRef: HTMLDivElement | undefined;

  const swipe = useEntrySwipeGestures({
    canSwipeLeft: () => true,
    canSwipeRight: () => true,
    onSwipeLeft: () => {
      props.onDelete(props.project);
    },
    onSwipeRight: () => {
      props.onEdit(props.project);
    },
    getSurfaceRef: () => surfaceRef,
  });

  const colors = () => getColorClass(props.project.color ?? "");

  return (
    // Swipe clip wrapper: data-no-swipe so the global page-swipe yields here.
    <div class="relative overflow-hidden rounded-[24px] touch-pan-y" data-no-swipe="true">
      {/* Action underlay revealed as the card slides. */}
      <div
        class="absolute inset-0 flex items-center justify-between px-6 rounded-[24px] pointer-events-none"
        aria-hidden="true"
      >
        <span
          class={cn(
            "flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-[#afc6ff] transition-opacity duration-150",
            swipe.tx() > 0 ? "opacity-100" : "opacity-0",
          )}
        >
          <Pencil class="h-4 w-4" />
          {t(msg`Edit`)}
        </span>
        <span
          class={cn(
            "flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-destructive transition-opacity duration-150",
            swipe.tx() < 0 ? "opacity-100" : "opacity-0",
          )}
        >
          {t(msg`Delete`)}
          <Trash2 class="h-4 w-4" />
        </span>
      </div>

      <div
        ref={(el) => {
          surfaceRef = el;
        }}
        class="relative z-10 bg-[#121212] rounded-[24px] border border-white/10 p-6 flex flex-col gap-4 overflow-hidden group hover:bg-white/[0.02] transition-colors duration-300 shadow-none"
        style={{
          transform: `translate3d(${swipe.tx()}px, 0, 0)`,
          transition: swipe.isAnimating() ? COMMIT_TRANSITION : "none",
        }}
        onPointerDown={swipe.handlePointerDown}
        onPointerMove={swipe.handlePointerMove}
        onPointerUp={swipe.handlePointerUp}
        onPointerCancel={swipe.handlePointerCancel}
      >
        {/* Accent border bar at top */}
        <div class={`absolute top-0 left-0 w-full h-1 ${colors().bar}`} />

        <div class="flex justify-between items-start">
          <div>
            <div class="flex items-center gap-1.5 mb-1.5">
              <span class={`w-2 h-2 rounded-full ${colors().bar}`} />
              <span class="font-mono text-xs text-muted-foreground uppercase tracking-wider">
                {props.project.code}
              </span>
            </div>
            <h3 class="text-base font-bold text-foreground font-mono line-clamp-1">
              {props.project.name}
            </h3>
          </div>
          <Popover open={props.isMenuOpen()} onOpenChange={props.setMenuOpen}>
            <PopoverTrigger>
              <button
                type="button"
                aria-label={t(msg`Project actions`)}
                data-no-entry-swipe="true"
                class="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-full hover:bg-white/5 cursor-pointer"
              >
                <MoreVertical class="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent class="bg-[#1a1a1a]/95 backdrop-blur-xl border border-white/10 rounded-[16px] p-1.5 shadow-2xl w-44 z-50">
              <button
                type="button"
                onClick={() => {
                  props.onEdit(props.project);
                }}
                class="w-full flex items-center gap-2 p-2 rounded-lg text-left hover:bg-white/5 transition-colors cursor-pointer text-xs font-mono text-foreground"
              >
                <Pencil class="h-3.5 w-3.5" /> {t(msg`Edit`)}
              </button>
              <button
                type="button"
                onClick={() => {
                  props.onTasks(props.project);
                }}
                class="w-full flex items-center gap-2 p-2 rounded-lg text-left hover:bg-white/5 transition-colors cursor-pointer text-xs font-mono text-foreground"
              >
                <ListTodo class="h-3.5 w-3.5" /> {t(msg`Tasks`)}
              </button>
              <button
                type="button"
                onClick={() => {
                  props.onToggleArchive(props.project);
                }}
                class="w-full flex items-center gap-2 p-2 rounded-lg text-left hover:bg-white/5 transition-colors cursor-pointer text-xs font-mono text-foreground"
              >
                <Archive class="h-3.5 w-3.5" />
                {props.project.status === "completed" ? t(msg`Reactivate`) : t(msg`Archive`)}
              </button>
              <button
                type="button"
                onClick={() => {
                  props.onDelete(props.project);
                }}
                class="w-full flex items-center gap-2 p-2 rounded-lg text-left hover:bg-destructive/10 transition-colors cursor-pointer text-xs font-mono text-destructive"
              >
                <Trash2 class="h-3.5 w-3.5" /> {t(msg`Delete`)}
              </button>
            </PopoverContent>
          </Popover>
        </div>

        <div class="my-2 flex-grow">
          <p class={`text-5xl font-bold tracking-tight font-mono leading-none ${colors().text}`}>
            {props.project.durationStr}
          </p>
          <p class="text-xs text-muted-foreground mt-2">{t(msg`weekly hrs`)}</p>
        </div>

        <div class="flex items-center justify-between mt-2 border-t border-white/5 pt-4">
          {/* Avatars */}
          <div class="flex -space-x-2 overflow-hidden">
            <For each={props.project.members.slice(0, MAX_VISIBLE_MEMBERS)}>
              {(m) => (
                <Avatar
                  userId={m.id}
                  name={m.name}
                  image={m.avatar}
                  size="xs"
                  class="w-7 h-7 rounded-full border-2 border-[#121212] text-[9px]"
                />
              )}
            </For>
            <Show when={props.project.members.length > MAX_VISIBLE_MEMBERS}>
              <div class="w-7 h-7 rounded-full border-2 border-[#121212] bg-[#1a1a1a] flex items-center justify-center text-[10px] font-mono text-muted-foreground">
                +{props.project.members.length - MAX_VISIBLE_MEMBERS}
              </div>
            </Show>
            <Show when={props.project.members.length === 0}>
              <span class="text-xs text-muted-foreground font-mono">{t(msg`No members`)}</span>
            </Show>
          </div>

          <span
            class={`font-mono text-xs px-2.5 py-0.5 rounded-full ${getStatusClass(props.project.status ?? "")}`}
          >
            {getStatusLabel(props.project.status ?? "")}
          </span>
        </div>
      </div>
    </div>
  );
}
